/**
 * Workshop Wrapper
 * Integrates WorkshopProvider and handles initial workshop selection
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { WorkshopProvider, useWorkshop } from "../contexts/WorkshopContext";
import WorkshopSelectionModal from "./WorkshopSelectionModal";

interface WorkshopWrapperProps {
  children: React.ReactNode;
}

// Inner component that uses WorkshopContext
const WorkshopModalManager: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { selectedWorkshop, workshops, setSelectedWorkshop, isLoading } =
    useWorkshop();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Show modal if user has workshops but none selected
    // and not loading anymore
    if (!isLoading && workshops.length > 0 && !selectedWorkshop) {
      setShowModal(true);
    }
  }, [isLoading, workshops, selectedWorkshop]);

  return (
    <>
      {children}

      {/* Initial Workshop Selection Modal */}
      <WorkshopSelectionModal
        workshops={workshops}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSelect={(workshop) => {
          setSelectedWorkshop(workshop);
          setShowModal(false);
        }}
        canClose={false} // User must select a workshop
      />
    </>
  );
};

// Outer wrapper that provides WorkshopContext
export const WorkshopWrapper: React.FC<WorkshopWrapperProps> = ({
  children,
}) => {
  const { workshops } = useAuth();

  return (
    <WorkshopProvider workshops={workshops}>
      <WorkshopModalManager>{children}</WorkshopModalManager>
    </WorkshopProvider>
  );
};

export default WorkshopWrapper;
